import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

interface ErrorLog {
  id: string;
  created_at: string;
  source: 'frontend' | 'backend' | 'supabase';
  error_type: string;
  message: string;
  stack_trace: string | null;
  user_id: string | null;
  metadata: any | null;
  is_resolved: boolean;
}

const ErrorsAdmin = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFilter, setCurrentFilter] = useState<'all' | 'frontend' | 'backend' | 'supabase'>('all');
  const [showResolved, setShowResolved] = useState(false);
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('errors')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (currentFilter !== 'all') {
        query = query.eq('source', currentFilter);
      }
      
      if (!showResolved) {
        query = query.eq('is_resolved', false);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setErrors(data || []);
    } catch (error) {
      console.error('Error fetching errors:', error);
      toast.error('Failed to load error logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchErrors();
  }, [currentFilter, showResolved]);

  const markAsResolved = async (id: string) => {
    try {
      const { error } = await supabase
        .from('errors')
        .update({ is_resolved: true })
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Error marked as resolved');
      fetchErrors();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update error status');
    }
  };

  const resolveAll = async () => {
    try {
      const { error } = await supabase
        .from('errors')
        .update({ is_resolved: true })
        .eq('is_resolved', false);
      
      if (error) throw error;
      toast.success('All errors marked as resolved');
      fetchErrors();
    } catch (error) {
      console.error('Error resolving all:', error);
      toast.error('Failed to resolve all errors');
    }
  };

  const showErrorDetails = (error: ErrorLog) => {
    setSelectedError(error);
    setDialogOpen(true);
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'frontend':
        return 'bg-blue-100 text-blue-800';
      case 'backend':
        return 'bg-purple-100 text-purple-800';
      case 'supabase':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Error Logs</h1>
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <span>Filter:</span>
              <Select 
                value={currentFilter}
                onValueChange={(value) => setCurrentFilter(value as any)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="frontend">Frontend</SelectItem>
                  <SelectItem value="backend">Backend</SelectItem>
                  <SelectItem value="supabase">Supabase</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox 
                id="showResolved" 
                checked={showResolved}
                onCheckedChange={() => setShowResolved(!showResolved)}
              />
              <label htmlFor="showResolved">Show Resolved</label>
            </div>

            <Button 
              onClick={resolveAll} 
              variant="outline"
              disabled={loading || errors.filter(e => !e.is_resolved).length === 0}
            >
              Resolve All
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center my-8">
            <div className="w-8 h-8 border-4 border-roundone-purple border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : errors.length === 0 ? (
          <div className="bg-gray-50 p-8 rounded-lg text-center">
            <p className="text-gray-500 font-medium">No errors found</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Error Type</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {errors.map((error) => (
                  <TableRow key={error.id}>
                    <TableCell>
                      {new Date(error.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getSourceColor(error.source)}>
                        {error.source}
                      </Badge>
                    </TableCell>
                    <TableCell>{error.error_type}</TableCell>
                    <TableCell className="max-w-xs truncate">
                      {error.message}
                    </TableCell>
                    <TableCell>
                      {error.is_resolved ? (
                        <Badge variant="outline" className="bg-green-100 text-green-800">
                          Resolved
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-100 text-red-800">
                          Unresolved
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => markAsResolved(error.id)}
                          disabled={error.is_resolved}
                          size="sm"
                          variant="outline"
                        >
                          Resolve
                        </Button>
                        <Button
                          onClick={() => showErrorDetails(error)}
                          size="sm"
                          variant="secondary"
                        >
                          Details
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Error Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Error Details</DialogTitle>
            <DialogDescription>
              Detailed information about the error
            </DialogDescription>
          </DialogHeader>

          {selectedError && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-sm">Time</h3>
                  <p>{new Date(selectedError.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Source</h3>
                  <Badge variant="outline" className={getSourceColor(selectedError.source)}>
                    {selectedError.source}
                  </Badge>
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Error Type</h3>
                  <p>{selectedError.error_type}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Status</h3>
                  {selectedError.is_resolved ? (
                    <Badge variant="outline" className="bg-green-100 text-green-800">
                      Resolved
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-100 text-red-800">
                      Unresolved
                    </Badge>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-sm">Message</h3>
                <p className="text-sm mt-1">{selectedError.message}</p>
              </div>

              {selectedError.stack_trace && (
                <div>
                  <h3 className="font-semibold text-sm">Stack Trace</h3>
                  <pre className="text-xs bg-gray-50 p-3 rounded-md overflow-x-auto mt-1 max-h-40">
                    {selectedError.stack_trace}
                  </pre>
                </div>
              )}

              {selectedError.metadata && (
                <div>
                  <h3 className="font-semibold text-sm">Additional Metadata</h3>
                  <pre className="text-xs bg-gray-50 p-3 rounded-md overflow-x-auto mt-1 max-h-40">
                    {JSON.stringify(selectedError.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedError.user_id && (
                <div>
                  <h3 className="font-semibold text-sm">User ID</h3>
                  <p className="text-sm font-mono mt-1">{selectedError.user_id}</p>
                </div>
              )}

              <div className="flex justify-end pt-4">
                {!selectedError.is_resolved && (
                  <Button 
                    onClick={() => {
                      markAsResolved(selectedError.id);
                      setDialogOpen(false);
                    }}
                    className="mr-2"
                  >
                    Mark as Resolved
                  </Button>
                )}
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ErrorsAdmin; 